import getInitials from "../utils/initials";
import { useId, useRef, useState } from "react";
import { getAvatarUri } from "../api";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { IconProp } from "@fortawesome/fontawesome-svg-core";
import { faCloudUploadAlt } from "@fortawesome/pro-solid-svg-icons";

type UserAvatarProp = {
  uid?: string;
  name?: string;
  size: number;
  initialsFontSize?: number;
  bgColor?: string;
  classname?: string;
  /**
   * Edge treatment: "white" is the classic outline around photos, "ring" the
   * semi-transparent hairline painted inside the edge (initials and other
   * non-photo variants never had the white outline, so "white" is a no-op
   * there). Decided per surface with design.
   */
  outline?: AvatarOutline;
};

type AvatarOutline = "white" | "ring";

type AvatarProp = UserAvatarProp & {
  isAnonymous: boolean;
  transferType?: "p2p" | "drop" | "mailbox" | "archive";
};

type LoadingAvatarProp = {
  size: number;
};

type AnonymousAvatarProp = LoadingAvatarProp;

type DropAvatarProp = LoadingAvatarProp;

export type InitialsProp = LoadingAvatarProp & {
  name: string;
  initialsFontSize?: number;
  bgColor?: string;
  classname?: string;
  /** Edge treatment; see {@link AvatarOutline}. */
  outline?: AvatarOutline;
};

type ProfileAvatarProp = LoadingAvatarProp & {
  onLoad: () => void;
  uid?: string;
  name?: string;
  /** Edge treatment; see {@link AvatarOutline}. */
  outline?: AvatarOutline;
  // Incase avatar fetching fails then InitialsAvatar
  // is shown. This field is to set the font size of
  // initials.
  initialsFontSize?: number;
  bgColor?: string;
  classname?: string;
};

export default function Avatar(avatarProp: AvatarProp) {
  // Show drop avatar if transfer type is drop
  if (avatarProp.transferType === "drop") {
    return <DropAvatar size={avatarProp.size} />;
  }

  // Show anonymous avatar if sender is anonymous
  if (avatarProp.isAnonymous) {
    return <AnonymousAvatar size={avatarProp.size} />;
  }

  // Otherwise show user avatar
  return (
    <UserAvatar
      uid={avatarProp.uid}
      name={avatarProp.name}
      size={avatarProp.size}
      initialsFontSize={avatarProp.initialsFontSize}
      bgColor={avatarProp.bgColor}
      classname={avatarProp.classname}
      outline={avatarProp.outline}
    />
  );
}

function UserAvatar(userAvatarProp: UserAvatarProp) {
  const [isLoading, setIsLoading] = useState<boolean>(true);
  // Legacy user
  if (!userAvatarProp.uid)
    return (
      <InitialsAvatar
        name={getInitials(userAvatarProp.name) ?? "--"}
        size={userAvatarProp.size}
        initialsFontSize={userAvatarProp.initialsFontSize}
        bgColor={userAvatarProp.bgColor}
        classname={userAvatarProp.classname}
      />
    );

  return (
    <>
      <div className={isLoading ? "" : "hidden"}>
        <LoadingAvatar size={userAvatarProp.size} />
      </div>
      <div className={isLoading ? "hidden" : "drag"}>
        <ProfileAvatar
          uid={userAvatarProp.uid}
          size={userAvatarProp.size}
          name={userAvatarProp.name}
          onLoad={() => {
            setIsLoading(false);
          }}
          initialsFontSize={userAvatarProp.initialsFontSize}
          bgColor={userAvatarProp.bgColor}
          classname={userAvatarProp.classname}
          outline={userAvatarProp.outline}
        />
      </div>
    </>
  );
}

/**
 * Returns the avatar set by the user(sender / recipient).
 * @param recipientAvatarProp
 * @returns a JSX element representing the actual recipient's avatar.
 */
function ProfileAvatar(profileAvatarProp: ProfileAvatarProp) {
  const isProfileAvatarAvailableRef = useRef<boolean>(true);

  const onLoad = () => profileAvatarProp.onLoad();
  const onError = () => {
    isProfileAvatarAvailableRef.current = false;
    profileAvatarProp.onLoad();
  };

  let dropShadow = "drop-shadow-[0_0px_3px_rgba(0,0,0,0.29)]";
  if (
    profileAvatarProp.classname &&
    profileAvatarProp.classname.includes("shadow")
  ) {
    dropShadow = "";
  }

  if (!isProfileAvatarAvailableRef.current) {
    return (
      <InitialsAvatar
        name={getInitials(profileAvatarProp.name) ?? "--"}
        size={profileAvatarProp.size}
        initialsFontSize={profileAvatarProp.initialsFontSize}
        bgColor={profileAvatarProp.bgColor}
        classname={profileAvatarProp.classname}
        outline={profileAvatarProp.outline}
      />
    );
  }
  const img = (
    <img
      draggable={false}
      className={`block aspect-square rounded-full ${
        profileAvatarProp.outline === "ring" ? "" : "border-[1px] border-white"
      } ${dropShadow} ${profileAvatarProp.classname}`}
      width={profileAvatarProp.size}
      height={profileAvatarProp.size}
      src={getAvatarUri(profileAvatarProp.uid ?? "")}
      onLoad={onLoad}
      onError={onError}
      alt={(profileAvatarProp.name ?? "--") + " avatar"}
    />
  );
  return profileAvatarProp.outline === "ring" ? (
    <span className="relative block w-fit">
      {img}
      <AvatarRing />
    </span>
  ) : (
    img
  );
}

/* Avatars with light edges would melt into light ground, so a semi
   transparent hairline is painted over the image's own edge pixels (the
   Facebook treatment) instead of adding a border around it, which would
   grow the disc and show as a bright halo on photos with dark edges. An
   overlay carries the inset ring because it renders under img pixels.
   Same recipe Facebook uses: https://ishadeed.com/article/new-facebook-css/ */
function AvatarRing() {
  return (
    <span
      aria-hidden
      className="pointer-events-none absolute inset-0 rounded-full ring-1 ring-inset ring-black/10"
    />
  );
}

/**
 * Returns the avatar with recipient's initials within.
 *
 * @param initialsProp
 * @returns a JSX element representing initials avatar.
 */
export function InitialsAvatar(initialsProp: InitialsProp) {
  let dropShadow = "drop-shadow-[0_0px_3px_rgba(0,0,0,0.29)]";
  if (initialsProp.classname && initialsProp.classname.includes("shadow")) {
    dropShadow = "";
  }

  return (
    <div
      className={`relative flex aspect-square items-center justify-center rounded-full font-ibm-plex-sans font-medium text-white ${initialsProp.classname} ${dropShadow}`}
      style={{
        minWidth: initialsProp.size,
        minHeight: initialsProp.size,
        backgroundColor: initialsProp.bgColor || "#666666",
      }}
    >
      <div
        style={{
          fontSize: initialsProp.initialsFontSize || 20,
        }}
      >
        {initialsProp.name}
      </div>
      {initialsProp.outline === "ring" && <AvatarRing />}
    </div>
  );
}

/**
 * Returns a dummy avatar which is shown when avatar is being loaded.
 *
 * @param loadingAvatarProp
 * @returns a JSX element representing loading avatar.
 */
function LoadingAvatar(loadingAvatarProp: LoadingAvatarProp) {
  return (
    <div
      className={`aspect-square animate-pulse rounded-full bg-[#E5E5E6]`}
      style={{
        minWidth: loadingAvatarProp.size,
        minHeight: loadingAvatarProp.size,
      }}
    ></div>
  );
}

/**
 * Returns a custom avatar which is shown when the transfer is sent via mailbox aka skp-light.
 *
 * @param anonymousAvatarProp
 * @returns a JSX element representing anonymous avatar.
 */
function AnonymousAvatar(anonymousAvatarProp: AnonymousAvatarProp) {
  // Icon size scales with avatar size, maintaining aspect ratio
  const iconSize = Math.round(anonymousAvatarProp.size * 0.5);

  const gradientStyle = {
    background:
      "linear-gradient(180deg, #3862AB 0%, #008BCB 96%, #8ABFE5 100%)",
    minHeight: anonymousAvatarProp.size,
    minWidth: anonymousAvatarProp.size,
  };

  return (
    <div
      className="flex aspect-square items-center justify-center rounded-full drop-shadow-md"
      style={gradientStyle}
    >
      <FontAwesomeIcon
        icon={faCloudUploadAlt as IconProp}
        className="text-white"
        style={{
          fontSize: iconSize,
        }}
      />
    </div>
  );
}

/**
 * Returns a custom avatar which is shown when the transfer type is drop.
 *
 * @param dropAvatarProp
 * @returns a JSX element representing drop avatar with chain link icon.
 */
function DropAvatar(dropAvatarProp: DropAvatarProp) {
  // Generate unique gradient ID to avoid conflicts if multiple drop avatars are rendered
  const uniqueId = useId();
  const gradientId = `drop-avatar-gradient-${uniqueId.replace(/:/g, "-")}`;

  return (
    <div
      className="flex aspect-square items-center justify-center overflow-hidden rounded-full drop-shadow-md"
      style={{
        minHeight: dropAvatarProp.size,
        minWidth: dropAvatarProp.size,
      }}
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width={dropAvatarProp.size}
        height={dropAvatarProp.size}
        viewBox="0 0 60 60"
        style={{ display: "block" }}
      >
        <defs>
          <linearGradient
            id={gradientId}
            x1="0.5"
            x2="0.5"
            y2="1"
            gradientUnits="objectBoundingBox"
          >
            <stop offset="0" stopColor="#3862ab" />
            <stop offset="1" stopColor="#008bcb" />
            <stop offset="1" stopColor="#8abfe5" />
          </linearGradient>
        </defs>
        <circle cx="30" cy="30" r="30" fill={`url(#${gradientId})`} />
        <path
          d="M15.957,10.043a7.119,7.119,0,0,1,.5,9.5c-.317.393-.141.2-4.313,4.368A7.109,7.109,0,0,1,2.084,13.861C3.749,12.2,4.717,11.226,5.3,10.639a.609.609,0,0,1,1.04.416,8.8,8.8,0,0,0,.241,1.921.606.606,0,0,1-.163.569L4.095,15.872a4.266,4.266,0,1,0,6.033,6.033l3.818-3.819a4.267,4.267,0,0,0-1.159-6.856.611.611,0,0,1-.333-.627,2.626,2.626,0,0,1,.752-1.544l.222-.222a.606.606,0,0,1,.7-.116,7.069,7.069,0,0,1,1.829,1.323Zm7.959-7.959a7.105,7.105,0,0,0-10.054,0c-4.172,4.172-4,3.976-4.313,4.368a7.11,7.11,0,0,0,2.325,10.827.606.606,0,0,0,.7-.116l.222-.222a2.626,2.626,0,0,0,.752-1.544.611.611,0,0,0-.333-.627,4.267,4.267,0,0,1-1.159-6.856l3.818-3.819a4.266,4.266,0,1,1,6.033,6.033l-2.328,2.328a.606.606,0,0,0-.163.569,8.8,8.8,0,0,1,.241,1.921.609.609,0,0,0,1.04.416c.588-.587,1.556-1.558,3.22-3.222a7.1,7.1,0,0,0,0-10.054Z"
          transform="translate(17 17)"
          fill="#fff"
        />
      </svg>
    </div>
  );
}
